/** Shared visual metadata for ticket status & priority + a relative-time helper. */
import { formatDistanceToNow } from "date-fns";
import { th } from "date-fns/locale";
import { Bug, CircleHelp, Lightbulb, Receipt, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TicketCategory, TicketPriority, TicketStatus } from "../types";

/** Translation record shape (the `support` namespace). */
type T = Record<string, string>;

export const STATUS_META: Record<TicketStatus, { dot: string; pill: string; key: string }> = {
  Open: {
    dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300",
    key: "statusOpen",
  },
  "In Progress": {
    dot: "bg-indigo-500",
    pill: "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300",
    key: "statusInProgress",
  },
  "Waiting on Customer": {
    dot: "bg-violet-500",
    pill: "bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300",
    key: "statusWaiting",
  },
  Resolved: {
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
    key: "statusResolved",
  },
  Closed: {
    dot: "bg-gray-400",
    pill: "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400",
    key: "statusClosed",
  },
};

export const PRIORITY_META: Record<TicketPriority, { dot: string; text: string; key: string }> = {
  Urgent: { dot: "bg-red-500", text: "text-red-600 dark:text-red-400", key: "priorityUrgent" },
  High: { dot: "bg-orange-500", text: "text-orange-600 dark:text-orange-400", key: "priorityHigh" },
  Medium: { dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400", key: "priorityMedium" },
  Low: { dot: "bg-gray-400", text: "text-gray-500 dark:text-gray-400", key: "priorityLow" },
};

const CATEGORY_KEY: Record<TicketCategory, string> = {
  Billing: "categoryBilling",
  Technical: "categoryTechnical",
  Bug: "categoryBug",
  "Feature Request": "categoryFeatureRequest",
  Other: "categoryOther",
};

export const statusLabel = (t: T, s: TicketStatus) => t[STATUS_META[s].key] ?? s;
export const priorityLabel = (t: T, p: TicketPriority) => t[PRIORITY_META[p].key] ?? p;
export const categoryLabel = (t: T, c: TicketCategory) => t[CATEGORY_KEY[c]] ?? c;

export function StatusPill({
  status,
  label,
  className,
}: {
  status: TicketStatus;
  label: string;
  className?: string;
}) {
  const m = STATUS_META[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        m.pill,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
      {label}
    </span>
  );
}

const CATEGORY_ICON: Record<TicketCategory, typeof Bug> = {
  Billing: Receipt,
  Technical: Wrench,
  Bug: Bug,
  "Feature Request": Lightbulb,
  Other: CircleHelp,
};

/** Category icon + label — for Select options/trigger. */
export function CategoryOption({ category, label }: { category: TicketCategory; label: string }) {
  const Icon = CATEGORY_ICON[category];
  return (
    <span className="inline-flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
      {label}
    </span>
  );
}

/** Priority dot + label — for Select options/trigger so priority reads at a glance. */
export function PriorityOption({ priority, label }: { priority: TicketPriority; label: string }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", PRIORITY_META[priority].dot)} />
      {label}
    </span>
  );
}

export function PriorityBadge({
  priority,
  label,
  className,
}: {
  priority: TicketPriority;
  label: string;
  className?: string;
}) {
  const m = PRIORITY_META[priority];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", m.text, className)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
      {label}
    </span>
  );
}

export function relativeTime(iso: string, lang?: string): string {
  try {
    return formatDistanceToNow(new Date(iso), {
      addSuffix: true,
      locale: lang === "th" ? th : undefined,
    });
  } catch {
    return "";
  }
}
